import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TableModule } from 'primeng/table';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { BaseComponent } from '../base/base.component';
import { PanelComponent } from '../core/panels/panel.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { ToolbarButtonComponent } from '../core/buttons/toolbar-button.component';
import { Api } from '../../services/api/api.service';
import { Session } from '../../services/session/session.service';
import { AlertEntry } from '../../models/api/qa-tools/alert-entry';
import { ReportLogEntry } from '../../models/api/qa-tools/report-log-entry';
import { ReportParamsEntry } from '../../models/api/qa-tools/report-params-entry';
import { DiffHighlighter } from '../../utilities/diff-highlighter';
import { DiffResult } from '../../models/diff-result';
import { QaParameterParser, QaParameterEntry } from './qa-parameter-parser';
import {
  QaParameterComparator,
  QaSectionComparison,
} from './qa-parameter-comparator';
import { ComparisonSummaryComponent } from './comparison-summary.component';
import { HomeCardComponent } from '../home/home-card.component';
import { HandleLoadingState } from '../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-qa-tools',
  imports: [
    CommonModule,
    PanelComponent,
    TableModule,
    MessageModule,
    SkeletonModule,
    ButtonComponent,
    ToolbarButtonComponent,
    DatePipe,
    ComparisonSummaryComponent,
    HomeCardComponent,
  ],
  template: `
    <div class="tw-p-6">
      <div
        class="tw-border tw-border-gray-300 tw-rounded-lg tw-overflow-hidden tw-px-4"
      >
        <app-panel label="Reports" [showExpand]="true" [expanded]="false">
          <div class="tw-p-4">
            <p class="tw-text-gray-600 tw-mb-4">
              View and compare report submissions for user:
              <strong>{{ session.user?.userName }}</strong>
            </p>

            @if (selectionLimitReached) {
              <p-message
                severity="warn"
                text="You can only select 2 reports at a time for comparison"
                styleClass="tw-mb-4"
              />
            }

            <div class="tw-flex tw-justify-between tw-items-center tw-mb-3">
              <app-toolbar-button
                icon="fa-light fa-arrows-rotate"
                ariaLabel="Refresh"
                (click)="refreshData()"
              />
              <app-button
                right1
                label="Compare Reports"
                [disabled]="selectedLogs.length !== 2 || comparingReports"
                (click)="compareReports()"
                ariaLabel="Compare selected reports"
              />
            </div>
            <div class="tw-grow tw-w-full tw-justify-center">
              <app-home-card type="Large">
                <div class="tw-pb-8 tw-w-full tw-min-w-0">
                  <app-panel header="Report Submissions">
                    @if (loading) {
                      <div class="tw-p-4 tw-space-y-3">
                        <p-skeleton height="3rem" />
                        <p-skeleton height="3rem" />
                        <p-skeleton height="3rem" />
                        <p-skeleton height="3rem" />
                        <p-skeleton height="3rem" />
                      </div>
                    } @else if (logs.length === 0) {
                      <div class="tw-text-center tw-py-8">
                        <p class="tw-text-gray-500">
                          No reports found for this user.
                        </p>
                      </div>
                    } @else {
                      <p-table
                        [value]="logs"
                        [(selection)]="selectedLogs"
                        (selectionChange)="onSelectionChange($event)"
                        [paginator]="true"
                        [rows]="10"
                        [rowsPerPageOptions]="[10, 25, 50]"
                        [stripedRows]="true"
                        dataKey="requestId"
                        styleClass="p-datatable-sm"
                      >
                        <ng-template pTemplate="header">
                          <tr>
                            <th style="width: 3rem"></th>
                            <th pSortableColumn="requestId">
                              Request ID <p-sortIcon field="requestId" />
                            </th>
                            <th pSortableColumn="sourceApp">
                              Source App <p-sortIcon field="sourceApp" />
                            </th>
                            <th pSortableColumn="requestName">
                              Request Name <p-sortIcon field="requestName" />
                            </th>
                            <th pSortableColumn="baseReportName">
                              Base Report Name
                              <p-sortIcon field="baseReportName" />
                            </th>
                            <th pSortableColumn="personalized">
                              Personalized <p-sortIcon field="personalized" />
                            </th>
                            <th pSortableColumn="createdDate">
                              Created Date <p-sortIcon field="createdDate" />
                            </th>
                          </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-log>
                          <tr>
                            <td>
                              <p-tableCheckbox
                                [value]="log"
                                [disabled]="
                                  selectedLogs.length >= 2 && !isSelected(log)
                                "
                              />
                            </td>
                            <td>{{ log.requestId }}</td>
                            <td>{{ log.sourceApp }}</td>
                            <td>{{ log.requestName }}</td>
                            <td>{{ log.baseReportName }}</td>
                            <td>{{ log.personalized }}</td>
                            <td>{{ log.createdDate | date: 'short' }}</td>
                          </tr>
                        </ng-template>
                      </p-table>

                      @if (selectedLogs.length > 0) {
                        <div class="tw-mt-4 tw-p-3 tw-bg-blue-50 tw-rounded">
                          <p class="tw-text-sm tw-text-blue-800">
                            {{ selectedLogs.length }} report(s) selected
                            @if (selectedLogs.length === 2) {
                              <span class="tw-font-semibold"
                                >- Ready for comparison</span
                              >
                            }
                          </p>
                        </div>
                      }
                    }
                  </app-panel>

                  @if (comparisonResult) {
                    <div class="tw-mt-6">
                      <!-- Comparison Summary Section -->
                      @if (comparisonSummary) {
                        <div class="tw-mb-6">
                          <app-comparison-summary
                            [sectionComparisons]="comparisonSummary"
                            [report1Type]="comparisonResult.report1.paramType"
                            [report2Type]="comparisonResult.report2.paramType"
                          />
                        </div>
                      }

                      <!-- Raw Comparison Section -->
                      <app-panel header="Raw Parameter Comparison">
                        <div class="tw-p-4">
                          <div
                            class="tw-flex tw-justify-between tw-items-center tw-mb-4"
                          >
                            <!-- Legend -->
                            <div class="tw-flex tw-gap-4 tw-text-sm">
                              <div class="tw-flex tw-items-center tw-gap-2">
                                <span
                                  class="tw-inline-block tw-w-4 tw-h-4 tw-rounded diff-added"
                                ></span>
                                <span>Added</span>
                              </div>
                              <div class="tw-flex tw-items-center tw-gap-2">
                                <span
                                  class="tw-inline-block tw-w-4 tw-h-4 tw-rounded diff-removed"
                                ></span>
                                <span>Removed</span>
                              </div>
                              <div class="tw-flex tw-items-center tw-gap-2">
                                <span
                                  class="tw-inline-block tw-w-4 tw-h-4 tw-rounded diff-modified"
                                ></span>
                                <span>Modified</span>
                              </div>
                            </div>

                            <app-button
                              right1
                              label="Clear Comparison"
                              (click)="clearComparison()"
                              ariaLabel="Clear comparison results"
                            />
                          </div>

                          <div
                            class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6"
                          >
                            <!-- Report 1 -->
                            <div
                              class="tw-border tw-border-gray-300 tw-rounded-lg"
                            >
                              <div
                                class="tw-bg-gray-100 tw-px-4 tw-py-3 tw-border-b tw-border-gray-300"
                              >
                                <div
                                  class="tw-flex tw-justify-between tw-items-start"
                                >
                                  <h3
                                    class="tw-font-semibold tw-text-lg tw-mb-2"
                                  >
                                    Report 1
                                  </h3>
                                  <app-toolbar-button
                                    icon="fa-light fa-copy"
                                    [ariaLabel]="
                                      'Copy ' +
                                      comparisonResult.report1.paramType
                                    "
                                    (click)="
                                      copyToClipboard(
                                        comparisonResult.report1.requestParams
                                      )
                                    "
                                    [title]="
                                      'Copy ' +
                                      comparisonResult.report1.paramType
                                    "
                                  />
                                </div>
                                <div
                                  class="tw-text-sm tw-text-gray-700 tw-space-y-1"
                                >
                                  <p>
                                    <strong>Name:</strong>
                                    {{ comparisonResult.report1.requestName }}
                                  </p>
                                  <p>
                                    <strong>Source:</strong>
                                    {{ comparisonResult.report1.sourceApp }}
                                  </p>
                                  <p>
                                    <strong>Created:</strong>
                                    {{
                                      comparisonResult.report1.createdDate
                                        | date: 'short'
                                    }}
                                  </p>
                                  <p>
                                    <strong>Format:</strong>
                                    {{ comparisonResult.report1.paramType }}
                                  </p>
                                </div>
                              </div>
                              <div class="tw-p-4 tw-bg-gray-50">
                                @if (diffResult) {
                                  <div
                                    class="diff-container tw-whitespace-pre-wrap tw-text-xs tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                    [innerHTML]="
                                      getSafeHtml(diffResult.leftHighlighted)
                                    "
                                  ></div>
                                } @else {
                                  <pre
                                    class="tw-whitespace-pre-wrap tw-text-xs tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                    >{{
                                      formatContent(
                                        comparisonResult.report1.requestParams,
                                        comparisonResult.report1.paramType
                                      )
                                    }}</pre
                                  >
                                }
                              </div>
                            </div>

                            <!-- Report 2 -->
                            <div
                              class="tw-border tw-border-gray-300 tw-rounded-lg"
                            >
                              <div
                                class="tw-bg-gray-100 tw-px-4 tw-py-3 tw-border-b tw-border-gray-300"
                              >
                                <div
                                  class="tw-flex tw-justify-between tw-items-start"
                                >
                                  <h3
                                    class="tw-font-semibold tw-text-lg tw-mb-2"
                                  >
                                    Report 2
                                  </h3>
                                  <app-toolbar-button
                                    icon="fa-light fa-copy"
                                    [ariaLabel]="
                                      'Copy ' +
                                      comparisonResult.report2.paramType
                                    "
                                    (click)="
                                      copyToClipboard(
                                        comparisonResult.report2.requestParams
                                      )
                                    "
                                    [title]="
                                      'Copy ' +
                                      comparisonResult.report2.paramType
                                    "
                                  />
                                </div>
                                <div
                                  class="tw-text-sm tw-text-gray-700 tw-space-y-1"
                                >
                                  <p>
                                    <strong>Name:</strong>
                                    {{ comparisonResult.report2.requestName }}
                                  </p>
                                  <p>
                                    <strong>Source:</strong>
                                    {{ comparisonResult.report2.sourceApp }}
                                  </p>
                                  <p>
                                    <strong>Created:</strong>
                                    {{
                                      comparisonResult.report2.createdDate
                                        | date: 'short'
                                    }}
                                  </p>
                                  <p>
                                    <strong>Format:</strong>
                                    {{ comparisonResult.report2.paramType }}
                                  </p>
                                </div>
                              </div>
                              <div class="tw-p-4 tw-bg-gray-50">
                                @if (diffResult) {
                                  <div
                                    class="diff-container tw-whitespace-pre-wrap tw-text-xs tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                    [innerHTML]="
                                      getSafeHtml(diffResult.rightHighlighted)
                                    "
                                  ></div>
                                } @else {
                                  <pre
                                    class="tw-whitespace-pre-wrap tw-text-xs tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                    >{{
                                      formatContent(
                                        comparisonResult.report2.requestParams,
                                        comparisonResult.report2.paramType
                                      )
                                    }}</pre
                                  >
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                      </app-panel>

                      <!-- Generated SQL Comparison Section -->
                      @if (generatedSqlComparison) {
                        <app-panel
                          header="Generated SQL Comparison"
                          class="tw-mt-6"
                        >
                          <div class="tw-p-4">
                            @if (isSqrOutput) {
                              <div
                                class="tw-mb-4 tw-p-3 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg"
                              >
                                <p class="tw-text-sm tw-text-blue-700">
                                  <strong>Note:</strong> This is SQR output (PDF
                                  report). The SQL below has been extracted from
                                  the SQR content.
                                </p>
                              </div>
                            }
                            <div
                              class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6"
                            >
                              <!-- Report 1 SQL -->
                              <div
                                class="tw-border tw-border-gray-300 tw-rounded-lg"
                              >
                                <div
                                  class="tw-bg-gray-100 tw-px-4 tw-py-3 tw-border-b tw-border-gray-300"
                                >
                                  <div
                                    class="tw-flex tw-justify-between tw-items-start"
                                  >
                                    <h3
                                      class="tw-font-semibold tw-text-lg tw-mb-2"
                                    >
                                      Report 1 - Generated SQL
                                    </h3>
                                    <app-toolbar-button
                                      icon="fa-light fa-copy"
                                      ariaLabel="Copy SQL"
                                      (click)="
                                        copyToClipboard(
                                          isSqrOutput
                                            ? extractedSqlComparison?.report1
                                            : generatedSqlComparison.report1
                                                ?.outputClob
                                        )
                                      "
                                      title="Copy SQL"
                                    />
                                  </div>
                                  <div
                                    class="tw-text-sm tw-text-gray-700 tw-space-y-1"
                                  >
                                    <p>
                                      <strong>Name:</strong>
                                      {{ comparisonResult.report1.requestName }}
                                    </p>
                                    <p>
                                      <strong>Source:</strong>
                                      {{ comparisonResult.report1.sourceApp }}
                                    </p>
                                    @if (
                                      generatedSqlComparison.report1
                                        ?.createdDate
                                    ) {
                                      <p>
                                        <strong>Generated:</strong>
                                        {{
                                          generatedSqlComparison.report1
                                            .createdDate | date: 'short'
                                        }}
                                      </p>
                                    }
                                  </div>
                                </div>
                                <div class="tw-p-4 tw-bg-gray-50">
                                  @if (loadingGeneratedSql) {
                                    <p-skeleton height="400px" />
                                  } @else if (
                                    isSqrOutput &&
                                    extractedSqlComparison?.report1
                                  ) {
                                    @if (extractedSqlDiff) {
                                      <div
                                        class="diff-container tw-whitespace-pre-wrap tw-text-xs tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                        [innerHTML]="
                                          getSafeHtml(
                                            extractedSqlDiff.leftHighlighted
                                          )
                                        "
                                      ></div>
                                    } @else {
                                      <pre
                                        class="tw-whitespace-pre-wrap tw-text-xs tw-font-mono tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                        >{{
                                          extractedSqlComparison.report1
                                        }}</pre
                                      >
                                    }
                                  } @else if (
                                    generatedSqlComparison.report1?.outputClob
                                  ) {
                                    @if (generatedSqlDiff) {
                                      <div
                                        class="diff-container tw-whitespace-pre-wrap tw-text-xs tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                        [innerHTML]="
                                          getSafeHtml(
                                            generatedSqlDiff.leftHighlighted
                                          )
                                        "
                                      ></div>
                                    } @else {
                                      <pre
                                        class="tw-whitespace-pre-wrap tw-text-xs tw-font-mono tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                        >{{
                                          generatedSqlComparison.report1
                                            .outputClob
                                        }}</pre
                                      >
                                    }
                                  } @else {
                                    <div
                                      class="tw-text-center tw-py-8 tw-text-gray-500 tw-bg-white tw-rounded tw-border tw-border-gray-200"
                                    >
                                      <p>No generated SQL available</p>
                                    </div>
                                  }
                                </div>
                              </div>

                              <!-- Report 2 SQL -->
                              <div
                                class="tw-border tw-border-gray-300 tw-rounded-lg"
                              >
                                <div
                                  class="tw-bg-gray-100 tw-px-4 tw-py-3 tw-border-b tw-border-gray-300"
                                >
                                  <div
                                    class="tw-flex tw-justify-between tw-items-start"
                                  >
                                    <h3
                                      class="tw-font-semibold tw-text-lg tw-mb-2"
                                    >
                                      Report 2 - Generated SQL
                                    </h3>
                                    <app-toolbar-button
                                      icon="fa-light fa-copy"
                                      ariaLabel="Copy SQL"
                                      (click)="
                                        copyToClipboard(
                                          isSqrOutput
                                            ? extractedSqlComparison?.report2
                                            : generatedSqlComparison.report2
                                                ?.outputClob
                                        )
                                      "
                                      title="Copy SQL"
                                    />
                                  </div>
                                  <div
                                    class="tw-text-sm tw-text-gray-700 tw-space-y-1"
                                  >
                                    <p>
                                      <strong>Name:</strong>
                                      {{ comparisonResult.report2.requestName }}
                                    </p>
                                    <p>
                                      <strong>Source:</strong>
                                      {{ comparisonResult.report2.sourceApp }}
                                    </p>
                                    @if (
                                      generatedSqlComparison.report2
                                        ?.createdDate
                                    ) {
                                      <p>
                                        <strong>Generated:</strong>
                                        {{
                                          generatedSqlComparison.report2
                                            .createdDate | date: 'short'
                                        }}
                                      </p>
                                    }
                                  </div>
                                </div>
                                <div class="tw-p-4 tw-bg-gray-50">
                                  @if (loadingGeneratedSql) {
                                    <p-skeleton height="400px" />
                                  } @else if (
                                    isSqrOutput &&
                                    extractedSqlComparison?.report2
                                  ) {
                                    @if (extractedSqlDiff) {
                                      <div
                                        class="diff-container tw-whitespace-pre-wrap tw-text-xs tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                        [innerHTML]="
                                          getSafeHtml(
                                            extractedSqlDiff.rightHighlighted
                                          )
                                        "
                                      ></div>
                                    } @else {
                                      <pre
                                        class="tw-whitespace-pre-wrap tw-text-xs tw-font-mono tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                        >{{
                                          extractedSqlComparison.report2
                                        }}</pre
                                      >
                                    }
                                  } @else if (
                                    generatedSqlComparison.report2?.outputClob
                                  ) {
                                    @if (generatedSqlDiff) {
                                      <div
                                        class="diff-container tw-whitespace-pre-wrap tw-text-xs tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                        [innerHTML]="
                                          getSafeHtml(
                                            generatedSqlDiff.rightHighlighted
                                          )
                                        "
                                      ></div>
                                    } @else {
                                      <pre
                                        class="tw-whitespace-pre-wrap tw-text-xs tw-font-mono tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                        >{{
                                          generatedSqlComparison.report2
                                            .outputClob
                                        }}</pre
                                      >
                                    }
                                  } @else {
                                    <div
                                      class="tw-text-center tw-py-8 tw-text-gray-500 tw-bg-white tw-rounded tw-border tw-border-gray-200"
                                    >
                                      <p>No generated SQL available</p>
                                    </div>
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        </app-panel>
                      }

                      <!-- Full SQR Comparison Section (only shown for SQR output) -->
                      @if (isSqrOutput && generatedSqlComparison) {
                        <app-panel header="Full SQR Comparison" class="tw-mt-6">
                          <div class="tw-p-4">
                            <div
                              class="tw-mb-4 tw-p-3 tw-bg-gray-100 tw-border tw-border-gray-300 tw-rounded-lg"
                            >
                              <p class="tw-text-sm tw-text-gray-700">
                                This section shows the complete SQR content for
                                comparison.
                              </p>
                            </div>
                            <div
                              class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6"
                            >
                              <!-- Report 1 Full SQR -->
                              <div
                                class="tw-border tw-border-gray-300 tw-rounded-lg"
                              >
                                <div
                                  class="tw-bg-gray-100 tw-px-4 tw-py-3 tw-border-b tw-border-gray-300"
                                >
                                  <div
                                    class="tw-flex tw-justify-between tw-items-start"
                                  >
                                    <h3
                                      class="tw-font-semibold tw-text-lg tw-mb-2"
                                    >
                                      Report 1 - Full SQR
                                    </h3>
                                    <app-toolbar-button
                                      icon="fa-light fa-copy"
                                      ariaLabel="Copy SQR"
                                      (click)="
                                        copyToClipboard(
                                          generatedSqlComparison.report1
                                            ?.outputClob
                                        )
                                      "
                                      title="Copy SQR"
                                    />
                                  </div>
                                  <div
                                    class="tw-text-sm tw-text-gray-700 tw-space-y-1"
                                  >
                                    <p>
                                      <strong>Name:</strong>
                                      {{ comparisonResult.report1.requestName }}
                                    </p>
                                    <p>
                                      <strong>Source:</strong>
                                      {{ comparisonResult.report1.sourceApp }}
                                    </p>
                                  </div>
                                </div>
                                <div class="tw-p-4 tw-bg-gray-50">
                                  @if (
                                    generatedSqlComparison.report1?.outputClob
                                  ) {
                                    @if (generatedSqlDiff) {
                                      <div
                                        class="diff-container tw-whitespace-pre-wrap tw-text-xs tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                        [innerHTML]="
                                          getSafeHtml(
                                            generatedSqlDiff.leftHighlighted
                                          )
                                        "
                                      ></div>
                                    } @else {
                                      <pre
                                        class="tw-whitespace-pre-wrap tw-text-xs tw-font-mono tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                        >{{
                                          generatedSqlComparison.report1
                                            .outputClob
                                        }}</pre
                                      >
                                    }
                                  } @else {
                                    <div
                                      class="tw-text-center tw-py-8 tw-text-gray-500 tw-bg-white tw-rounded tw-border tw-border-gray-200"
                                    >
                                      <p>No SQR content available</p>
                                    </div>
                                  }
                                </div>
                              </div>

                              <!-- Report 2 Full SQR -->
                              <div
                                class="tw-border tw-border-gray-300 tw-rounded-lg"
                              >
                                <div
                                  class="tw-bg-gray-100 tw-px-4 tw-py-3 tw-border-b tw-border-gray-300"
                                >
                                  <div
                                    class="tw-flex tw-justify-between tw-items-start"
                                  >
                                    <h3
                                      class="tw-font-semibold tw-text-lg tw-mb-2"
                                    >
                                      Report 2 - Full SQR
                                    </h3>
                                    <app-toolbar-button
                                      icon="fa-light fa-copy"
                                      ariaLabel="Copy SQR"
                                      (click)="
                                        copyToClipboard(
                                          generatedSqlComparison.report2
                                            ?.outputClob
                                        )
                                      "
                                      title="Copy SQR"
                                    />
                                  </div>
                                  <div
                                    class="tw-text-sm tw-text-gray-700 tw-space-y-1"
                                  >
                                    <p>
                                      <strong>Name:</strong>
                                      {{ comparisonResult.report2.requestName }}
                                    </p>
                                    <p>
                                      <strong>Source:</strong>
                                      {{ comparisonResult.report2.sourceApp }}
                                    </p>
                                  </div>
                                </div>
                                <div class="tw-p-4 tw-bg-gray-50">
                                  @if (
                                    generatedSqlComparison.report2?.outputClob
                                  ) {
                                    @if (generatedSqlDiff) {
                                      <div
                                        class="diff-container tw-whitespace-pre-wrap tw-text-xs tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                        [innerHTML]="
                                          getSafeHtml(
                                            generatedSqlDiff.rightHighlighted
                                          )
                                        "
                                      ></div>
                                    } @else {
                                      <pre
                                        class="tw-whitespace-pre-wrap tw-text-xs tw-font-mono tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                                        >{{
                                          generatedSqlComparison.report2
                                            .outputClob
                                        }}</pre
                                      >
                                    }
                                  } @else {
                                    <div
                                      class="tw-text-center tw-py-8 tw-text-gray-500 tw-bg-white tw-rounded tw-border tw-border-gray-200"
                                    >
                                      <p>No SQR content available</p>
                                    </div>
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        </app-panel>
                      }
                    </div>
                  }
                </div>
              </app-home-card>
            </div>
          </div>
        </app-panel>
      </div>

      <div
        class="tw-border tw-border-gray-300 tw-rounded-lg tw-overflow-hidden tw-px-4 tw-mt-6"
      >
        <app-panel label="Alerts" [showExpand]="true" [expanded]="false">
          <div class="tw-p-4">
            <p class="tw-text-gray-600 tw-mb-4">
              View and compare alert definitions for user:
              <strong>{{ session.user?.userName }}</strong>
            </p>

            @if (alertSelectionLimitReached) {
              <p-message
                severity="warn"
                text="You can only select 2 alerts at a time for comparison"
                styleClass="tw-mb-4"
              />
            }

            <div class="tw-flex tw-justify-between tw-items-center tw-mb-3">
              <app-toolbar-button
                icon="fa-light fa-arrows-rotate"
                ariaLabel="Refresh Alerts"
                (click)="refreshAlerts()"
              />
              <app-button
                right1
                label="Compare Alerts"
                [disabled]="selectedAlerts.length !== 2 || comparingAlerts"
                (click)="compareAlerts()"
                ariaLabel="Compare selected alerts"
              />
            </div>

            <app-panel header="User Alerts">
              @if (loadingAlerts) {
                <div class="tw-p-4 tw-space-y-3">
                  <p-skeleton height="3rem" />
                  <p-skeleton height="3rem" />
                  <p-skeleton height="3rem" />
                </div>
              } @else if (alerts.length === 0) {
                <div class="tw-text-center tw-py-8">
                  <p class="tw-text-gray-500">No alerts found for this user.</p>
                </div>
              } @else {
                <p-table
                  [value]="alerts"
                  [(selection)]="selectedAlerts"
                  (selectionChange)="onAlertSelectionChange($event)"
                  [paginator]="true"
                  [rows]="10"
                  [rowsPerPageOptions]="[10, 25, 50]"
                  [stripedRows]="true"
                  dataKey="alertId"
                  styleClass="p-datatable-sm"
                >
                  <ng-template pTemplate="header">
                    <tr>
                      <th style="width: 3rem"></th>
                      <th pSortableColumn="alertId">
                        Alert ID <p-sortIcon field="alertId" />
                      </th>
                      <th pSortableColumn="userId">
                        User ID <p-sortIcon field="userId" />
                      </th>
                      <th pSortableColumn="description">
                        Description <p-sortIcon field="description" />
                      </th>
                      <th pSortableColumn="enabled">
                        Enabled <p-sortIcon field="enabled" />
                      </th>
                      <th pSortableColumn="startDate">
                        Start Date <p-sortIcon field="startDate" />
                      </th>
                      <th pSortableColumn="endDate">
                        End Date <p-sortIcon field="endDate" />
                      </th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-alert>
                    <tr>
                      <td>
                        <p-tableCheckbox
                          [value]="alert"
                          [disabled]="
                            selectedAlerts.length >= 2 &&
                            !isAlertSelected(alert)
                          "
                        />
                      </td>
                      <td>{{ alert.alertId }}</td>
                      <td>{{ alert.userId }}</td>
                      <td>{{ alert.description }}</td>
                      <td>{{ alert.enabled }}</td>
                      <td>{{ alert.startDate | date: 'short' }}</td>
                      <td>{{ alert.endDate | date: 'short' }}</td>
                    </tr>
                  </ng-template>
                </p-table>

                @if (selectedAlerts.length > 0) {
                  <div class="tw-mt-4 tw-p-3 tw-bg-blue-50 tw-rounded">
                    <p class="tw-text-sm tw-text-blue-800">
                      {{ selectedAlerts.length }} alert(s) selected
                      @if (selectedAlerts.length === 2) {
                        <span class="tw-font-semibold"
                          >- Ready for comparison</span
                        >
                      }
                    </p>
                  </div>
                }
              }
            </app-panel>

            @if (alertDiffResult) {
              <div class="tw-mt-6">
                <app-panel header="Alert XML Comparison">
                  <div class="tw-p-4">
                    <div
                      class="tw-flex tw-justify-between tw-items-center tw-mb-4"
                    >
                      <div class="tw-flex tw-gap-4 tw-text-sm">
                        <div class="tw-flex tw-items-center tw-gap-2">
                          <span
                            class="tw-inline-block tw-w-4 tw-h-4 tw-rounded diff-added"
                          ></span>
                          <span>Added</span>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-2">
                          <span
                            class="tw-inline-block tw-w-4 tw-h-4 tw-rounded diff-removed"
                          ></span>
                          <span>Removed</span>
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-2">
                          <span
                            class="tw-inline-block tw-w-4 tw-h-4 tw-rounded diff-modified"
                          ></span>
                          <span>Modified</span>
                        </div>
                      </div>

                      <app-button
                        right1
                        label="Clear Comparison"
                        (click)="clearAlertComparison()"
                        ariaLabel="Clear alert comparison results"
                      />
                    </div>

                    <div
                      class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6"
                    >
                      <!-- Alert 1 -->
                      <div class="tw-border tw-border-gray-300 tw-rounded-lg">
                        <div
                          class="tw-bg-gray-100 tw-px-4 tw-py-3 tw-border-b tw-border-gray-300"
                        >
                          <div
                            class="tw-flex tw-justify-between tw-items-start"
                          >
                            <h3 class="tw-font-semibold tw-text-lg tw-mb-2">
                              Alert 1
                            </h3>
                            <app-toolbar-button
                              icon="fa-light fa-copy"
                              ariaLabel="Copy Alert XML"
                              (click)="
                                copyToClipboard(
                                  alertComparisonPair!.alert1.alertData
                                )
                              "
                              title="Copy Alert XML"
                            />
                          </div>
                          <div class="tw-text-sm tw-text-gray-700 tw-space-y-1">
                            <p>
                              <strong>Alert ID:</strong>
                              {{ alertComparisonPair!.alert1.alertId }}
                            </p>
                            <p>
                              <strong>Description:</strong>
                              {{ alertComparisonPair!.alert1.description }}
                            </p>
                          </div>
                        </div>
                        <div class="tw-p-4 tw-bg-gray-50">
                          <div
                            class="diff-container tw-whitespace-pre-wrap tw-text-xs tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                            [innerHTML]="
                              getSafeHtml(alertDiffResult.leftHighlighted)
                            "
                          ></div>
                        </div>
                      </div>

                      <!-- Alert 2 -->
                      <div class="tw-border tw-border-gray-300 tw-rounded-lg">
                        <div
                          class="tw-bg-gray-100 tw-px-4 tw-py-3 tw-border-b tw-border-gray-300"
                        >
                          <div
                            class="tw-flex tw-justify-between tw-items-start"
                          >
                            <h3 class="tw-font-semibold tw-text-lg tw-mb-2">
                              Alert 2
                            </h3>
                            <app-toolbar-button
                              icon="fa-light fa-copy"
                              ariaLabel="Copy Alert XML"
                              (click)="
                                copyToClipboard(
                                  alertComparisonPair!.alert2.alertData
                                )
                              "
                              title="Copy Alert XML"
                            />
                          </div>
                          <div class="tw-text-sm tw-text-gray-700 tw-space-y-1">
                            <p>
                              <strong>Alert ID:</strong>
                              {{ alertComparisonPair!.alert2.alertId }}
                            </p>
                            <p>
                              <strong>Description:</strong>
                              {{ alertComparisonPair!.alert2.description }}
                            </p>
                          </div>
                        </div>
                        <div class="tw-p-4 tw-bg-gray-50">
                          <div
                            class="diff-container tw-whitespace-pre-wrap tw-text-xs tw-bg-white tw-p-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-auto tw-max-h-[600px]"
                            [innerHTML]="
                              getSafeHtml(alertDiffResult.rightHighlighted)
                            "
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </app-panel>
              </div>
            }
          </div>
        </app-panel>
      </div>
    </div>
  `,
  styles: `
    ::ng-deep .p-datatable .p-datatable-thead > tr > th {
      background-color: var(--sed-grey-10-color);
      color: var(--sed-engaged-black-color);
      border-color: var(--sed-grey-40-color);
      font-weight: 600;
    }

    ::ng-deep
      .p-datatable
      .p-datatable-thead
      > tr
      > th
      .p-sortable-column-icon {
      color: var(--sed-engaged-black-color);
    }

    ::ng-deep .diff-added {
      background-color: #d4edda;
      color: #155724;
      padding: 2px 0;
    }

    ::ng-deep .diff-removed {
      background-color: #f8d7da;
      color: #721c24;
      padding: 2px 0;
    }

    ::ng-deep .diff-modified {
      background-color: #fff3cd;
      color: #856404;
      padding: 2px 0;
    }

    ::ng-deep .diff-unchanged {
      background-color: transparent;
      padding: 2px 0;
    }

    ::ng-deep .diff-container > div {
      font-family: 'Courier New', monospace;
      line-height: 1.5;
    }
  `,
})
export class QaToolsComponent extends BaseComponent implements OnInit {
  protected override api = inject(Api);
  protected override session = inject(Session);
  private sanitizer = inject(DomSanitizer);

  logs: ReportLogEntry[] = [];
  selectedLogs: ReportLogEntry[] = [];
  loading = false;
  selectionLimitReached = false;
  comparisonResult: {
    report1: ReportParamsEntry;
    report2: ReportParamsEntry;
  } | null = null;
  comparingReports = false;
  diffResult: DiffResult | null = null;
  comparisonSummary: QaSectionComparison[] | null = null;
  generatedSqlComparison: any = null;
  loadingGeneratedSql = false;
  generatedSqlDiff: DiffResult | null = null;
  extractedSqlComparison: {
    report1: string | null;
    report2: string | null;
  } | null = null;
  extractedSqlDiff: DiffResult | null = null;
  isSqrOutput = false;

  // Alerts state
  alerts: AlertEntry[] = [];
  selectedAlerts: AlertEntry[] = [];
  loadingAlerts = false;
  alertSelectionLimitReached = false;
  comparingAlerts = false;
  alertDiffResult: DiffResult | null = null;
  alertComparisonPair: {
    alert1: AlertEntry;
    alert2: AlertEntry;
  } | null = null;

  @HandleLoadingState()
  async ngOnInit() {
    await Promise.all([this.loadReportsLog(), this.loadAlerts()]);
  }

  @HandleLoadingState()
  async loadReportsLog(refreshCache = false) {
    const userName = this.session.user?.userName;
    if (!userName) {
      return;
    }

    const response = await this.api.qaTools.reportsLog(userName, refreshCache);
    this.logs = response.logs || [];
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async refreshData() {
    this.selectedLogs = [];
    this.clearComparison();
    await this.loadReportsLog(true);
  }

  onSelectionChange(selection: ReportLogEntry[]) {
    if (selection.length > 2) {
      // Revert to only the first 2 selected items
      this.selectedLogs = selection.slice(0, 2);
      this.selectionLimitReached = true;
      // Hide the warning after 3 seconds
      setTimeout(() => {
        this.selectionLimitReached = false;
      }, 3000);
    } else {
      this.selectionLimitReached = false;
    }
  }

  isSelected(log: ReportLogEntry): boolean {
    return this.selectedLogs.some(
      (selected) => selected.requestId === log.requestId,
    );
  }

  @HandleLoadingState({
    loadingProperty: 'comparingReports',
    errorProperty: 'actionError',
  })
  async compareReports() {
    if (this.selectedLogs.length !== 2) {
      return;
    }

    this.comparingReports = true;
    try {
      const response = await this.api.qaTools.compareReports(
        this.selectedLogs[0].requestId,
        this.selectedLogs[1].requestId,
      );
      this.comparisonResult = response;

      // Parse parameters for structured comparison using QA-specific parser
      const params1 =
        response.report1.paramType === 'JSON'
          ? QaParameterParser.parseJson(response.report1.requestParams)
          : QaParameterParser.parseXml(response.report1.requestParams);

      const params2 =
        response.report2.paramType === 'JSON'
          ? QaParameterParser.parseJson(response.report2.requestParams)
          : QaParameterParser.parseXml(response.report2.requestParams);

      // Generate comparison summary using QA-specific comparator
      this.comparisonSummary = QaParameterComparator.compare(params1, params2);

      // Calculate diff highlighting for raw view
      const text1 = this.formatContent(
        response.report1.requestParams,
        response.report1.paramType,
      );
      const text2 = this.formatContent(
        response.report2.requestParams,
        response.report2.paramType,
      );

      this.diffResult = DiffHighlighter.compareText(text1, text2);

      // Fetch generated SQL comparison
      this.loadingGeneratedSql = true;
      try {
        this.generatedSqlComparison =
          await this.api.qaTools.compareGeneratedSql(
            this.selectedLogs[0].requestId,
            this.selectedLogs[1].requestId,
          );

        // Determine output format from parsed report parameters
        const getOutputFormat = (entries: QaParameterEntry[]): string => {
          const entry = entries.find(
            (e) => e.path.toLowerCase() === 'outputs.option.output_format',
          );
          return entry ? String(entry.value).toUpperCase() : '';
        };

        const report1IsPdf = getOutputFormat(params1) === 'PDF';
        const report2IsPdf = getOutputFormat(params2) === 'PDF';
        this.isSqrOutput = report1IsPdf || report2IsPdf;

        if (this.isSqrOutput) {
          // PDF output - extract SQL from SQR content and show Full SQR panel
          const extractedSql1 =
            report1IsPdf && this.generatedSqlComparison?.report1?.outputClob
              ? this.extractSqlFromSqr(
                  this.generatedSqlComparison.report1.outputClob,
                )
              : this.generatedSqlComparison?.report1?.outputClob || null;
          const extractedSql2 =
            report2IsPdf && this.generatedSqlComparison?.report2?.outputClob
              ? this.extractSqlFromSqr(
                  this.generatedSqlComparison.report2.outputClob,
                )
              : this.generatedSqlComparison?.report2?.outputClob || null;

          this.extractedSqlComparison = {
            report1: extractedSql1,
            report2: extractedSql2,
          };

          if (extractedSql1 && extractedSql2) {
            this.extractedSqlDiff = DiffHighlighter.compareText(
              extractedSql1,
              extractedSql2,
            );
          }

          // Full SQR diff
          if (
            this.generatedSqlComparison?.report1?.outputClob &&
            this.generatedSqlComparison?.report2?.outputClob
          ) {
            this.generatedSqlDiff = DiffHighlighter.compareText(
              this.generatedSqlComparison.report1.outputClob,
              this.generatedSqlComparison.report2.outputClob,
            );
          }
        } else {
          // Non-PDF output - show raw SQL only (no SQR panels)
          // Clear any SQR-related data so templates don't render them
          this.extractedSqlComparison = null;
          this.extractedSqlDiff = null;

          if (
            this.generatedSqlComparison?.report1?.outputClob &&
            this.generatedSqlComparison?.report2?.outputClob
          ) {
            this.generatedSqlDiff = DiffHighlighter.compareText(
              this.generatedSqlComparison.report1.outputClob,
              this.generatedSqlComparison.report2.outputClob,
            );
          }
        }
      } catch {
        // Error loading generated SQL
      } finally {
        this.loadingGeneratedSql = false;
      }
    } catch {
      // Error comparing reports
    } finally {
      this.comparingReports = false;
    }
  }

  clearComparison() {
    this.comparisonResult = null;
    this.diffResult = null;
    this.comparisonSummary = null;
    this.generatedSqlComparison = null;
    this.generatedSqlDiff = null;
    this.extractedSqlComparison = null;
    this.extractedSqlDiff = null;
    this.isSqrOutput = false;
    this.selectedLogs = [];
  }

  // ── Alerts methods ──

  async loadAlerts(refreshCache = false) {
    const userName = this.session.user?.userName;
    if (!userName) {
      return;
    }

    this.loadingAlerts = true;
    try {
      const response = await this.api.qaTools.alertsList(
        userName,
        refreshCache,
      );
      this.alerts = response.alerts || [];
    } catch {
      this.alerts = [];
    } finally {
      this.loadingAlerts = false;
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async refreshAlerts() {
    this.selectedAlerts = [];
    this.clearAlertComparison();
    await this.loadAlerts(true);
  }

  onAlertSelectionChange(selection: AlertEntry[]) {
    if (selection.length > 2) {
      this.selectedAlerts = selection.slice(0, 2);
      this.alertSelectionLimitReached = true;
      setTimeout(() => {
        this.alertSelectionLimitReached = false;
      }, 3000);
    } else {
      this.alertSelectionLimitReached = false;
    }
  }

  isAlertSelected(alert: AlertEntry): boolean {
    return this.selectedAlerts.some((s) => s.alertId === alert.alertId);
  }

  compareAlerts() {
    if (this.selectedAlerts.length !== 2) {
      return;
    }

    this.comparingAlerts = true;
    try {
      const xml1 = this.formatXml(this.selectedAlerts[0].alertData || '');
      const xml2 = this.formatXml(this.selectedAlerts[1].alertData || '');

      this.alertComparisonPair = {
        alert1: this.selectedAlerts[0],
        alert2: this.selectedAlerts[1],
      };

      this.alertDiffResult = DiffHighlighter.compareText(xml1, xml2);
    } finally {
      this.comparingAlerts = false;
    }
  }

  clearAlertComparison() {
    this.alertDiffResult = null;
    this.alertComparisonPair = null;
    this.selectedAlerts = [];
  }

  private extractSqlFromSqr(sqrContent: string): string | null {
    const startMarker = 'ADD 1 TO #_records_returned';
    const fromMarker = 'FROM (';
    const selectMarker = 'SELECT';

    const startIdx = sqrContent.indexOf(startMarker);
    if (startIdx === -1) {
      return null;
    }

    // Find the FROM ( after the start marker
    const fromIdx = sqrContent.indexOf(fromMarker, startIdx);
    if (fromIdx === -1) {
      return null;
    }

    // Find SELECT after FROM ( - this is where the actual SQL starts
    const selectIdx = sqrContent.indexOf(selectMarker, fromIdx);
    if (selectIdx === -1) {
      return null;
    }

    // Find the end of the SQL - look for closing parens followed by WHERE 1=1
    // Try multiple patterns to handle different whitespace/newline combinations
    const contentAfterSelect = sqrContent.substring(selectIdx);

    // Try pattern 1: ) followed by whitespace followed by WHERE 1=1
    let endPattern = /\)\s+WHERE 1=1/;
    let endMatch = endPattern.exec(contentAfterSelect);

    // Try pattern 2: ) followed by newline (possibly with \r) and WHERE 1=1
    if (!endMatch) {
      endPattern = /\)\s*[\r\n]+\s*WHERE 1=1/;
      endMatch = endPattern.exec(contentAfterSelect);
    }

    // Try pattern 3: Look for just WHERE 1=1 on its own line and backtrack
    if (!endMatch) {
      endPattern = /[\r\n]\s*WHERE 1=1/;
      endMatch = endPattern.exec(contentAfterSelect);
    }

    let extractedSql: string;
    if (endMatch) {
      // Extract up to the ) before WHERE 1=1
      // This captures the complete inner query including all necessary closing parens
      extractedSql = contentAfterSelect.substring(0, endMatch.index);
    } else {
      // Fallback: look for END-SELECT
      const endSelectIdx = contentAfterSelect.indexOf('END-SELECT');
      if (endSelectIdx === -1) {
        return null;
      }
      extractedSql = contentAfterSelect.substring(0, endSelectIdx);
      // Clean up trailing WHERE 1=1 and ORDER BY if present
      extractedSql = extractedSql.replace(/\s*WHERE 1=1[\s\S]*$/, '');
      extractedSql = extractedSql.replace(/\s*ORDER BY[\s\S]*$/, '');
    }

    // Add SELECT * FROM( prefix and closing ) to make it a complete SQL statement
    const result = 'SELECT * FROM(\n' + extractedSql.trim() + '\n)';
    return result;
  }

  formatJson(json: string): string {
    try {
      return JSON.stringify(JSON.parse(json), null, 4);
    } catch {
      return json;
    }
  }

  formatXml(xml: string): string {
    // Basic XML formatting - just add indentation
    return xml;
  }

  formatContent(content: string, paramType: string): string {
    if (paramType.toLowerCase() === 'json') {
      return this.formatJson(content);
    } else if (paramType.toLowerCase() === 'xml') {
      return this.formatXml(content);
    }
    return content;
  }

  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.sanitize(1, html) || '';
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async copyToClipboard(content: string | null | undefined): Promise<void> {
    if (!content) return;
    await navigator.clipboard.writeText(content);
  }
}
